import type { ReactElement } from "react"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import Media from "@gatsby-plugin-generic-page/components/Media"
import Surface from "@gatsby-plugin-generic-page/components/Surface"
import TextWithPrice from "@gatsby-plugin-generic-page/components/TextWithPrice"
import type { TabContentCardSectionFlatData } from "@gatsby-plugin-generic-page/fragments/tabContentCardSection"
import { getBgColorClass } from "@gatsby-plugin-generic-page/fragments/udsBackgroundColor"
import { Text } from "@ionos-web-design-system/react"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import useHelThemeData from "~/hooks/useHelThemeData"
import isEmpty from "~/lib/isEmpty"

export default function TabContentCardSection({
  caption,
  title,
  textWithPrice,
  ctaElement,
  description,
  image,
  backgroundColor,
  shadow,
  sectionSettings,
}: TabContentCardSectionFlatData): ReactElement<TabContentCardSectionFlatData> {
  const { isDarkOnColorValue } = useHelThemeData()
  const boxBgColorClass = getBgColorClass(backgroundColor)
  const isInvertedBackground =
    backgroundColor?.color.endsWith("-invert") ?? false
  // "-invert" UDS tokens flip relative to the ambient color scheme, so the
  // text must invert the same way, not always force "dark".
  const invertedColorScheme = isDarkOnColorValue ? "light" : "dark"

  const cardContent = (
    <>
      {!isEmpty(caption) ? (
        <Text variant="bodySm" className="uppercase" color="base">
          {caption}
        </Text>
      ) : null}

      <Text asChild variant="heading2xl" weight="black" color="base">
        <h2>{title}</h2>
      </Text>

      {!isEmpty(textWithPrice) || !isEmpty(ctaElement) ? (
        <div className="flex gap-4 flex-wrap justify-between items-center mt-5">
          {!isEmpty(textWithPrice) ? (
            <div>
              <TextWithPrice {...textWithPrice} size="medium" />
            </div>
          ) : null}

          {!isEmpty(ctaElement) ? <CtaElement ctaElement={ctaElement} /> : null}
        </div>
      ) : null}

      {!isEmpty(description) ? (
        <div className="mt-8">
          <MarkdownText>{description}</MarkdownText>
        </div>
      ) : null}
    </>
  )

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <div className="flex flex-col-reverse md:flex-row gap-4 items-stretch">
          <Surface
            className="flex-1 p-5 lg:p-6"
            bgColorClass={boxBgColorClass}
            shadow={shadow}
          >
            {isInvertedBackground ? (
              <UdsThemeProvider forceColorScheme={invertedColorScheme}>
                {cardContent}
              </UdsThemeProvider>
            ) : (
              cardContent
            )}
          </Surface>

          {!isEmpty(image) ? (
            <div className="flex-1 rounded-sm overflow-hidden [&_picture]:block [&_picture]:h-full [&_img]:!h-full [&_img]:w-full [&_img]:object-cover">
              <Media media={image} />
            </div>
          ) : null}
        </div>
      </BaseContainer>
    </BaseSection>
  )
}
